import { CalendarClock, Lock } from 'lucide-react'
import { cn } from '@/utils/cn'
import {
  agendarFieldIconWrapClass,
  agendarFieldLabelClass,
  agendarFieldShellClass,
} from './agendarReuniaoFieldStyles'
import {
  getMeetingTypeOption,
  meetingTypesForContext,
  meetingTypesForDealStage,
  type MeetingTypeValue,
} from './meetingTypeOptions'

type Props = {
  value: string
  onChange: (value: MeetingTypeValue) => void
  context: 'leadConvert' | 'deal'
  dealStage?: string
  variant?: 'default' | 'compact'
  showDisabledOptions?: boolean
  stepLabel?: string
  disabled?: boolean
}

export function MeetingTypeSelector({
  value,
  onChange,
  context,
  dealStage,
  variant = 'default',
  showDisabledOptions = context === 'leadConvert' && variant !== 'compact',
  stepLabel,
  disabled = false,
}: Props) {
  const allowed = dealStage
    ? meetingTypesForDealStage(dealStage)
    : meetingTypesForContext(context)
  const selected = getMeetingTypeOption(value)
  const disabledOptions = showDisabledOptions
    ? meetingTypesForContext('deal').filter((option) => !option.allowedOnLeadConvert)
    : []

  if (variant === 'compact') {
    return (
      <div className={cn('space-y-1.5', disabled && 'opacity-50 pointer-events-none')}>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-text-label">
          {stepLabel ?? 'Tipo de reunião'}
        </p>
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Tipo de reunião">
          {allowed.map((option) => {
            const active = value === option.value
            const title =
              context === 'leadConvert' && option.convertLabel
                ? option.convertLabel
                : option.shortLabel
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={active}
                title={option.description}
                onClick={() => onChange(option.value)}
                className={cn(
                  'rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors',
                  active
                    ? 'border-accent bg-accent/15 text-text-primary ring-1 ring-accent/30'
                    : 'border-white/[0.08] bg-white/[0.03] text-text-secondary hover:border-accent/25 hover:text-text-primary',
                )}
              >
                {title}
              </button>
            )
          })}
        </div>
        {selected ? (
          <p className="text-[11px] leading-snug text-text-muted">{selected.description}</p>
        ) : null}
        {disabled ? (
          <p className="text-[11px] text-text-muted">Escolha a data antes de definir o tipo.</p>
        ) : null}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className={cn('flex items-start gap-3', agendarFieldShellClass)}>
        <span className={agendarFieldIconWrapClass}>
          <CalendarClock size={18} aria-hidden />
        </span>
        <div className="min-w-0 flex-1 space-y-2">
          <p className={agendarFieldLabelClass}>Tipo de reunião</p>

          <div
            className="grid grid-cols-1 gap-1.5 sm:grid-cols-2"
            role="radiogroup"
            aria-label="Tipo de reunião"
          >
            {allowed.map((option) => {
              const active = value === option.value
              const title =
                context === 'leadConvert' && option.convertLabel
                  ? option.convertLabel
                  : option.label
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => onChange(option.value)}
                  className={cn(
                    'rounded-lg border px-3 py-2 text-left transition-colors',
                    active
                      ? 'border-accent bg-accent/15 ring-1 ring-accent/40'
                      : 'border-white/[0.08] bg-white/[0.03] hover:border-accent/30 hover:bg-white/[0.05]',
                  )}
                >
                  <span className="block text-sm font-semibold text-text-primary">{title}</span>
                  <span className="mt-0.5 block text-[11px] leading-snug text-text-muted">
                    {option.description}
                  </span>
                </button>
              )
            })}
          </div>

          {disabledOptions.length > 0 && (
            <div className="space-y-1.5 border-t border-white/[0.06] pt-2">
              <p className="text-[10px] font-medium uppercase tracking-wider text-text-muted">
                Próximas etapas do pipeline
              </p>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {disabledOptions.map((option) => (
                  <div
                    key={option.value}
                    className="rounded-lg border border-dashed border-white/[0.08] bg-white/[0.02] px-3 py-2 opacity-70"
                  >
                    <div className="flex items-center gap-1.5">
                      <Lock size={12} className="shrink-0 text-text-muted" aria-hidden />
                      <span className="text-sm font-medium text-text-secondary">{option.shortLabel}</span>
                    </div>
                    <p className="mt-0.5 text-[11px] leading-snug text-text-muted">
                      {option.disabledReason}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selected?.description && (
            <p className="text-[11px] leading-relaxed text-text-secondary">{selected.description}</p>
          )}
        </div>
      </div>
    </div>
  )
}
